/**
 * TreatmentReadyChip — M16B-07B
 *
 * Pure presentation component. Renders ONE per-treatment-slug status
 * pill (🟢 Treatment Ready / 🟡 Additional Review Required / 🔴 Blocked)
 * plus an optional human-readable blocker breakdown.
 *
 * STRICT RULES (drift-guarded):
 *  - Does NO readiness calculation. The readiness verdict is consumed
 *    verbatim from the canonical M16B-07A API response.
 *  - Treatment-slug level only. NEVER aggregates to a single
 *    "lead ready" / "patient ready" / "clinic ready" verdict.
 *  - Patient mode shows only human-readable blocker phrases; clinic
 *    mode may surface evidence (offer ID, recommendation, etc.).
 */
import React from 'react';

// ── Static, schema-level vocabulary (matches backend constants;
//    no business metadata, no recommendation/audit/status vocabulary).
const STATUS_COLOR = {
  ready:   { dot: '🟢', label: 'Treatment Ready',           pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { dot: '🟡', label: 'Additional Review Required',  pill: 'bg-amber-50 text-amber-800 border-amber-200' },
  blocked: { dot: '🔴', label: 'Blocked',                    pill: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function classify(verdict) {
  if (verdict?.treatment_ready === true) return 'ready';
  const blockerCount = (verdict?.missing?.length || verdict?.blockers?.length || 0);
  if (blockerCount === 0) return 'pending';
  return 'pending';
}

function humanizeSlug(slug) {
  if (!slug) return '';
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function TreatmentReadyChip({ verdict, mode = 'patient', showBlockers = true }) {
  if (!verdict) return null;
  const cls = classify(verdict);
  const palette = STATUS_COLOR[cls];
  const slug = verdict.treatment_slug;
  const missing = verdict.missing || (verdict.blockers || []).map((b) => b.patient_message).filter(Boolean);
  const lastTx = verdict.last_transition;

  // Build a human-readable "Last readiness transition" caption. The
  // patient-mode payload arrives already redacted (`redact_transition_for_patient`);
  // clinic-mode shows the raw event name without IDs.
  let txCaption = null;
  if (lastTx) {
    const at = (lastTx.at || lastTx.computed_at || '').slice(0, 10);
    if (lastTx.headline) {
      // Patient-redacted shape.
      txCaption = `${lastTx.headline}${at ? ` on ${at}` : ''}`;
    } else if (lastTx.event === 'became_ready') {
      txCaption = `Last readiness transition: became ready${at ? ` on ${at}` : ''}`;
    } else if (lastTx.event === 'became_not_ready') {
      txCaption = `Last readiness transition: review re-opened${at ? ` on ${at}` : ''}`;
    }
  }

  return (
    <div
      data-testid={`treatment-ready-chip-${slug}`}
      data-treatment-slug={slug}
      data-treatment-ready={verdict.treatment_ready ? 'true' : 'false'}
      className={`inline-flex flex-col gap-1 rounded-xl border px-3 py-2 ${palette.pill}`}
    >
      <div className="flex items-center gap-2">
        <span aria-hidden>{palette.dot}</span>
        <span className="font-medium text-sm">{humanizeSlug(slug)}</span>
        <span className="text-xs opacity-70">·</span>
        <span data-testid={`treatment-ready-status-${slug}`} className="text-xs">
          {palette.label}
        </span>
      </div>
      {txCaption && (
        <div
          data-testid={`treatment-ready-last-transition-${slug}`}
          className="text-[11px] opacity-80"
        >
          {txCaption}
        </div>
      )}
      {showBlockers && cls !== 'ready' && missing && missing.length > 0 && (
        <ul
          data-testid={`treatment-ready-missing-${slug}`}
          className="ml-6 list-disc text-xs space-y-0.5 opacity-90"
        >
          {missing.map((m, i) => (
            <li key={`${slug}-miss-${i}`}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export { STATUS_COLOR, classify, humanizeSlug };
